import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { workshopApi } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { ScrollArea } from '../../components/ui/scroll-area';
import {
  ArrowLeft, Phone, PhoneOff, MicOff, Users, Zap, Clock, AlertTriangle,
  Activity, Radio, RefreshCw, Pause, Play, ChevronRight, Mic, Hash,
  PhoneCall, PhoneIncoming, PhoneOutgoing,
} from 'lucide-react';
import { toast } from 'sonner';

const formatDuration = (startIso) => {
  if (!startIso) return '—';
  const ms = Date.now() - new Date(startIso).getTime();
  if (ms < 0) return '0s';
  const s = Math.floor(ms / 1000);
  const mm = Math.floor(s / 60);
  const ss = s % 60;
  if (mm < 60) return `${mm}:${String(ss).padStart(2, '0')}`;
  const hh = Math.floor(mm / 60);
  return `${hh}:${String(mm % 60).padStart(2, '0')}:${String(ss).padStart(2, '0')}`;
};

const LatencyDot = ({ ms }) => {
  if (ms == null) return <span className="w-1.5 h-1.5 rounded-full bg-zinc-400 inline-block" />;
  const c = ms < 100 ? 'bg-emerald-500' : ms < 300 ? 'bg-amber-500' : 'bg-red-500';
  return <span className={`w-1.5 h-1.5 rounded-full ${c} inline-block`} title={`${ms}ms`} />;
};


export default function LiveWorkshopDashboard() {
  const { exerciseId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [, setTick] = useState(0); // forces re-render for duration timers
  const intervalRef = useRef(null);

  const fetchLive = useCallback(async () => {
    try {
      const d = await workshopApi.getLive(exerciseId);
      setData(d);
    } catch (e) {
      console.error('workshop live fetch failed', e);
    } finally {
      setLoading(false);
    }
  }, [exerciseId]);

  useEffect(() => { fetchLive(); }, [fetchLive]);

  useEffect(() => {
    if (!autoRefresh) return;
    const id = setInterval(fetchLive, 3000);
    return () => clearInterval(id);
  }, [autoRefresh, fetchLive]);

  // Tick every second to update call duration timers
  useEffect(() => {
    intervalRef.current = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(intervalRef.current);
  }, []);

  const handleHangupAll = async () => {
    if (!window.confirm('Hang up ALL active calls in this exercise? This affects every participant.')) return;
    try {
      const r = await workshopApi.hangupAll(exerciseId);
      toast.success(`Hangup-all sent. ${r.telnyx_calls_terminated || 0} Telnyx call(s) terminated.`);
      fetchLive();
    } catch (e) {
      toast.error(`Failed: ${e.message || e}`);
    }
  };

  const handleMuteAll = async () => {
    if (!window.confirm('Mute every participant\'s microphone?')) return;
    try {
      await workshopApi.muteAll(exerciseId);
      toast.success('Mute-all signal sent');
    } catch (e) {
      toast.error(`Failed: ${e.message || e}`);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <RefreshCw className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }
  if (!data) return null;

  const { exercise, summary, participants, internal_calls, telnyx_calls, upcoming_injects, recent_injects } = data;

  return (
    <div className="min-h-screen bg-background" data-testid="live-workshop-dashboard">
      {/* Header */}
      <div className="border-b border-border bg-card sticky top-0 z-20 backdrop-blur-sm">
        <div className="max-w-[1800px] mx-auto px-4 py-3 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate(`/exercises/${exerciseId}`)} data-testid="workshop-back-btn">
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <div>
              <h1 className="text-lg font-bold flex items-center gap-2">
                <Radio className="w-4 h-4 text-emerald-500 animate-pulse" />
                Live Workshop
              </h1>
              <p className="text-xs text-muted-foreground">
                {exercise.name} · <span className="capitalize">{exercise.status}</span>
                {exercise.started_at && <> · started {formatDuration(exercise.started_at)} ago</>}
              </p>
            </div>
          </div>
          <div className="flex gap-2 items-center">
            <Button variant={autoRefresh ? 'default' : 'outline'} size="sm" onClick={() => setAutoRefresh(!autoRefresh)} data-testid="workshop-autorefresh-toggle">
              {autoRefresh ? <Activity className="w-3.5 h-3.5 mr-1.5 animate-pulse" /> : <Pause className="w-3.5 h-3.5 mr-1.5" />}
              {autoRefresh ? 'Live' : 'Paused'}
            </Button>
            <Button variant="outline" size="sm" onClick={handleMuteAll} data-testid="workshop-mute-all-btn">
              <MicOff className="w-3.5 h-3.5 mr-1.5" />
              Mute All
            </Button>
            <Button variant="destructive" size="sm" onClick={handleHangupAll} data-testid="workshop-hangup-all-btn">
              <PhoneOff className="w-3.5 h-3.5 mr-1.5" />
              Hangup All
            </Button>
          </div>
        </div>
      </div>

      {/* Stat strip */}
      <div className="max-w-[1800px] mx-auto px-4 py-4 grid grid-cols-2 md:grid-cols-6 gap-3">
        <StatCard icon={<Users className="w-4 h-4" />} label="Online" value={`${summary.online}/${summary.expected}`} testId="stat-online" tone={summary.online >= summary.expected * 0.8 ? 'good' : 'warn'} />
        <StatCard icon={<Hash className="w-4 h-4" />} label="Extensions" value={summary.extensions_assigned} testId="stat-extensions" />
        <StatCard icon={<PhoneCall className="w-4 h-4" />} label="Internal Calls" value={summary.internal_calls_active} testId="stat-internal-calls" tone={summary.internal_calls_active > 0 ? 'live' : 'idle'} />
        <StatCard icon={<PhoneIncoming className="w-4 h-4" />} label="Telnyx Calls" value={summary.telnyx_calls_active} testId="stat-telnyx-calls" tone={summary.telnyx_calls_active > 0 ? 'live' : 'idle'} />
        <StatCard icon={<Zap className="w-4 h-4" />} label="Pending Injects" value={summary.injects_pending} testId="stat-pending-injects" />
        <StatCard icon={<Clock className="w-4 h-4" />} label="Last Refresh" value={autoRefresh ? '3s' : 'paused'} testId="stat-refresh" />
      </div>

      <div className="max-w-[1800px] mx-auto px-4 pb-8 grid grid-cols-1 xl:grid-cols-3 gap-4">
        {/* Participants column */}
        <Card className="xl:col-span-1">
          <CardHeader className="py-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Users className="w-4 h-4" /> Participants ({summary.online}/{summary.expected})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <ScrollArea className="h-[420px]">
              {participants.length === 0 ? (
                <div className="p-8 text-center text-sm text-muted-foreground">No participants assigned</div>
              ) : participants.map((p) => (
                <div key={p.user_id} className="flex items-center gap-2 px-4 py-2 border-b last:border-b-0 hover:bg-muted/30" data-testid={`participant-${p.user_id}`}>
                  <span className={`w-2 h-2 rounded-full shrink-0 ${p.online ? 'bg-emerald-500 animate-pulse' : 'bg-zinc-400'}`} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{p.name}</p>
                    <p className="text-xs text-muted-foreground truncate">{p.role_name || (p.roles || []).join(', ')}</p>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    {p.extension && (
                      <Badge variant="secondary" className="font-mono text-[10px] h-5">ext {p.extension}</Badge>
                    )}
                    {p.online && <LatencyDot ms={p.latency_ms} />}
                  </div>
                </div>
              ))}
            </ScrollArea>
          </CardContent>
        </Card>

        {/* Active Calls column */}
        <Card className="xl:col-span-1">
          <CardHeader className="py-3">
            <CardTitle className="text-base flex items-center gap-2">
              <PhoneCall className="w-4 h-4" /> Active Calls
              {(internal_calls.length + telnyx_calls.length) > 0 && (
                <Badge className="bg-emerald-500/15 text-emerald-700 border-emerald-500/30 animate-pulse">LIVE</Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <ScrollArea className="h-[420px]">
              {(internal_calls.length === 0 && telnyx_calls.length === 0) ? (
                <div className="p-8 text-center text-sm text-muted-foreground">No active calls</div>
              ) : (
                <>
                  {telnyx_calls.map((c) => (
                    <div key={c.log_id || c.call_control_id} className="px-4 py-2.5 border-b last:border-b-0 bg-emerald-500/5" data-testid={`call-telnyx-${c.log_id}`}>
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2 min-w-0 flex-1">
                          {c.direction === 'outbound' ? <PhoneOutgoing className="w-3.5 h-3.5 text-blue-500 shrink-0" /> : <PhoneIncoming className="w-3.5 h-3.5 text-emerald-500 shrink-0" />}
                          <div className="min-w-0">
                            <p className="text-sm font-mono truncate">{c.from_number} → {c.to_number}</p>
                            <p className="text-xs text-muted-foreground capitalize">Telnyx · {c.routing_mode || c.direction} · {c.status}</p>
                          </div>
                        </div>
                        <Badge variant="outline" className="font-mono text-xs shrink-0">{formatDuration(c.started_at)}</Badge>
                      </div>
                    </div>
                  ))}
                  {internal_calls.map((c) => (
                    <div key={c.log_id || c.call_id} className="px-4 py-2.5 border-b last:border-b-0" data-testid={`call-internal-${c.log_id || c.call_id}`}>
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2 min-w-0 flex-1">
                          <Phone className="w-3.5 h-3.5 text-blue-500 shrink-0" />
                          <div className="min-w-0">
                            <p className="text-sm font-mono truncate">ext {c.from_extension || '?'} → ext {c.to_extension || '?'}</p>
                            <p className="text-xs text-muted-foreground">Internal PBX · {c.status || 'active'}</p>
                          </div>
                        </div>
                        <Badge variant="outline" className="font-mono text-xs shrink-0">{formatDuration(c.start_time)}</Badge>
                      </div>
                    </div>
                  ))}
                </>
              )}
            </ScrollArea>
          </CardContent>
        </Card>

        {/* Inject queue column */}
        <Card className="xl:col-span-1">
          <CardHeader className="py-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Zap className="w-4 h-4" /> Inject Queue
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <ScrollArea className="h-[420px]">
              {upcoming_injects.length === 0 && recent_injects.length === 0 ? (
                <div className="p-8 text-center text-sm text-muted-foreground">No injects scheduled</div>
              ) : (
                <>
                  {upcoming_injects.length > 0 && (
                    <div className="px-4 py-2 bg-muted/40 text-xs font-medium text-muted-foreground uppercase tracking-wider">Upcoming ({upcoming_injects.length})</div>
                  )}
                  {upcoming_injects.map((i) => (
                    <div key={i.inject_id} className="px-4 py-2 border-b last:border-b-0 hover:bg-muted/30 flex items-center gap-2" data-testid={`inject-upcoming-${i.inject_id}`}>
                      <Clock className="w-3.5 h-3.5 text-amber-500 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm truncate">{i.title}</p>
                        <p className="text-xs text-muted-foreground capitalize">{i.category || ''} · {i.status}</p>
                      </div>
                      <Badge variant="outline" className="text-xs shrink-0">{i.delivery_time ? new Date(i.delivery_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '—'}</Badge>
                    </div>
                  ))}
                  {recent_injects.length > 0 && (
                    <div className="px-4 py-2 mt-2 bg-muted/40 text-xs font-medium text-muted-foreground uppercase tracking-wider">Recent</div>
                  )}
                  {recent_injects.map((i) => (
                    <div key={i.inject_id} className="px-4 py-2 border-b last:border-b-0 flex items-center gap-2 opacity-75" data-testid={`inject-recent-${i.inject_id}`}>
                      <ChevronRight className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm truncate">{i.title}</p>
                        <p className="text-xs text-muted-foreground capitalize">{i.status}</p>
                      </div>
                      <span className="text-xs text-muted-foreground shrink-0">{i.triggered_at ? formatDuration(i.triggered_at) : ''}</span>
                    </div>
                  ))}
                </>
              )}
            </ScrollArea>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}


function StatCard({ icon, label, value, testId, tone }) {
  const toneCls = {
    good: 'text-emerald-600 dark:text-emerald-400',
    warn: 'text-amber-600 dark:text-amber-400',
    bad: 'text-red-600 dark:text-red-400',
    live: 'text-emerald-500',
    idle: 'text-zinc-400',
  }[tone] || '';

  return (
    <Card data-testid={testId}>
      <CardContent className="p-3 flex items-center gap-3">
        <div className={`w-9 h-9 rounded-md flex items-center justify-center bg-muted/50 ${toneCls}`}>
          {icon}
        </div>
        <div className="min-w-0">
          <p className="text-xs text-muted-foreground">{label}</p>
          <p className={`text-xl font-bold leading-tight ${toneCls}`}>{value}</p>
        </div>
      </CardContent>
    </Card>
  );
}
